import React from 'react';
import { Card } from '../ui/card';
import { Award, Crown, Check, Star, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { loyaltyLevels } from '../../data/mock';

const iconMap = {
  Award,
  Crown
};

const Loyalty = () => {
  return (
    <section id="loyalty" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-[#6B2C91] font-semibold text-sm uppercase tracking-wider">
            Programme Fidélité
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-gray-900">
            Gagnez à Chaque Transaction
          </h2>
          <p className="mt-4 text-lg text-gray-600">
            Le Programme de Fidélité Le Plus Généreux de Côte d'Ivoire. 
            Chaque franc dépensé dans l'écosystème EKIP vous rapproche de vos objectifs.
          </p>
        </div>

        {/* How Points Work */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          <div className="bg-gradient-to-br from-[#6B2C91]/5 to-[#6B2C91]/10 rounded-2xl p-6">
            <div className="w-12 h-12 bg-[#6B2C91] rounded-xl flex items-center justify-center mb-4">
              <span className="text-white font-bold">W</span>
            </div>
            <h3 className="font-bold text-gray-900 mb-2">Chez Wayne</h3>
            <p className="text-sm text-gray-600 mb-4">Stations-Services</p>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Bronze</span>
                <span className="font-semibold">1 pt / 1 000 FCFA</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Silver</span>
                <span className="font-semibold">1,5 pts / 1 000 FCFA</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Gold</span>
                <span className="font-semibold">2 pts / 1 000 FCFA</span>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-[#0066CC]/5 to-[#0066CC]/10 rounded-2xl p-6">
            <div className="w-12 h-12 bg-[#0066CC] rounded-xl flex items-center justify-center mb-4">
              <span className="text-white font-bold">E</span>
            </div>
            <h3 className="font-bold text-gray-900 mb-2">Chez EKIPTECH</h3>
            <p className="text-sm text-gray-600 mb-4">Technologies</p>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Bronze</span>
                <span className="font-semibold">2 pts / 1 000 FCFA</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Silver</span>
                <span className="font-semibold">3 pts / 1 000 FCFA</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Gold</span>
                <span className="font-semibold">4 pts / 1 000 FCFA</span>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-[#FFD700]/5 to-[#FFD700]/20 rounded-2xl p-6">
            <div className="w-12 h-12 bg-[#FFD700] rounded-xl flex items-center justify-center mb-4">
              <span className="text-gray-900 font-bold">K</span>
            </div>
            <h3 className="font-bold text-gray-900 mb-2">Chez KIDH</h3>
            <p className="text-sm text-gray-600 mb-4">Immobilier</p>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Bronze</span>
                <span className="font-semibold">1 pt / 10 000 FCFA</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Silver</span>
                <span className="font-semibold">1,5 pts / 10 000 FCFA</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Gold</span>
                <span className="font-semibold">2 pts / 10 000 FCFA</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bonus Section */}
        <div className="bg-gradient-to-r from-[#6B2C91] to-[#5a2579] rounded-3xl p-8 mb-16">
          <div className="grid md:grid-cols-2 gap-8 text-white">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <Star className="w-6 h-6 text-[#FFD700]" />
              </div>
              <div>
                <h3 className="font-bold text-lg mb-1">Achat Croisé</h3>
                <p className="text-white/80">+50% points quand vous achetez dans 2 enseignes différentes le même mois</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <span className="text-[#FFD700] font-bold">x2</span>
              </div>
              <div>
                <h3 className="font-bold text-lg mb-1">Triple Play</h3>
                <p className="text-white/80">x2 tous vos points si vous achetez chez Wayne + EKIPTECH + KIDH dans le même trimestre</p>
              </div>
            </div>
          </div>
        </div>

        {/* Loyalty Levels */}
        <div className="grid md:grid-cols-3 gap-8">
          {loyaltyLevels.map((level, index) => {
            const Icon = iconMap[level.icon];
            return (
              <Card
                key={index}
                className="relative overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                {/* Top accent */}
                <div 
                  className="h-2" 
                  style={{ backgroundColor: level.color }}
                />
                
                <div className="p-6">
                  {/* Icon and Level */}
                  <div className="flex items-center gap-3 mb-4">
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${level.color}20` }}
                    >
                      <Icon className="w-6 h-6" style={{ color: level.color }} />
                    </div>
                    <div>
                      <h3 className="font-bold text-xl text-gray-900">Niveau {level.level}</h3>
                    </div>
                  </div>

                  {/* Requirement */}
                  <p className="text-sm text-gray-600 mb-6 pb-4 border-b border-gray-100">
                    {level.requirement}
                  </p>

                  {/* Benefits */}
                  <ul className="space-y-3">
                    {level.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-start text-sm text-gray-700">
                        <Check className="w-4 h-4 text-[#00C853] mr-2 flex-shrink-0 mt-0.5" />
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Card>
            );
          })}
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <Button
            size="lg"
            className="bg-[#6B2C91] hover:bg-[#5a2579] text-white px-8 group"
          >
            Voir le Catalogue Récompenses
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Loyalty;
